import React from 'react';
import { MessageSquare, Mail, X, Send } from 'lucide-react';

/**
 * Template Selection Modal
 * Displays available Email/WhatsApp templates and sends the selected one.
 */
export default function TemplateSelectionModal({
  templateType,
  templates,
  selectedTemplate,
  onTemplateSelect,
  messagePreview,
  replacePlaceholders,
  formData,
  onClose,
  onSend,
}) {
  const filteredTemplates = templates.filter(t => t.type === templateType);
  const isWhatsApp = templateType === 'whatsapp';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="template-selection-modal">
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className={`p-4 flex items-center justify-between text-white ${isWhatsApp ? 'bg-green-500' : ''}`} style={!isWhatsApp ? { background: 'linear-gradient(135deg, #4338CA 0%, #4F46E5 50%, #6D28D9 100%)' } : {}}>
          <div className="flex items-center gap-3">
            {isWhatsApp ? <MessageSquare className="w-6 h-6" /> : <Mail className="w-6 h-6" />}
            <h3 className="font-bold text-lg">
              Send {isWhatsApp ? 'WhatsApp' : 'Email'} - Registration Update
            </h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded" data-testid="template-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Template List */}
          <div className="w-1/2 border-r overflow-y-auto p-4">
            <h4 className="font-semibold text-gray-700 mb-3">Select Template</h4>
            {filteredTemplates.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <p>No {templateType} templates found.</p>
                <a href="/sales/templates" className="text-blue-500 underline mt-2 inline-block">
                  Create templates
                </a>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredTemplates.map(template => (
                  <div
                    key={template.id}
                    onClick={() => onTemplateSelect(template)}
                    data-testid={`template-option-${template.id}`}
                    className={`p-3 rounded-lg cursor-pointer border transition-all ${
                      selectedTemplate?.id === template.id
                        ? isWhatsApp
                          ? 'border-green-500 bg-green-50'
                          : 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <h5 className="font-medium text-gray-800">{template.name}</h5>
                    {template.description && (
                      <p className="text-xs text-gray-500 mt-1">{template.description}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Preview */}
          <div className="w-1/2 p-4 bg-gray-50 overflow-y-auto">
            <h4 className="font-semibold text-gray-700 mb-3">Message Preview</h4>
            {selectedTemplate ? (
              <div className="space-y-3">
                {!isWhatsApp && selectedTemplate.subject && (
                  <div className="p-3 bg-white rounded-lg border">
                    <span className="text-xs text-gray-500">Subject:</span>
                    <p className="font-medium">{replacePlaceholders(selectedTemplate.subject)}</p>
                  </div>
                )}
                <div className="p-3 bg-white rounded-lg border whitespace-pre-wrap">
                  {messagePreview}
                </div>
                <div className="text-xs text-gray-500 mt-2">
                  <strong>Sending to:</strong>{' '}
                  {isWhatsApp
                    ? (formData.mobile_no_1 || formData.whatsapp_no_1)
                    : (formData.email || 'N/A')}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                Select a template to preview
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
            data-testid="template-modal-cancel"
          >
            Cancel
          </button>
          <button
            onClick={onSend}
            disabled={!selectedTemplate}
            data-testid="template-modal-send"
            className={`px-4 py-2 text-white rounded-xl flex items-center gap-2 transition-all duration-180 ease-out hover:scale-[1.02] active:scale-95 ${
              isWhatsApp
                ? 'bg-green-500 hover:bg-green-600 disabled:bg-green-300'
                : 'shadow-[0_4px_12px_rgba(79,70,229,0.30)] hover:shadow-[0_8px_20px_rgba(79,70,229,0.40)] disabled:opacity-50 disabled:scale-100'
            }`}
            style={!isWhatsApp ? { background: 'linear-gradient(135deg, #4F46E5 0%, #6D28D9 100%)' } : {}}
          >
            <Send className="w-4 h-4" />
            Send {isWhatsApp ? 'WhatsApp' : 'Email'}
          </button>
        </div>
      </div>
    </div>
  );
}
